import React from "react";
import "./RewardsPage.css";
import RewardCard, { RewardCardSkeleton, CoinIcon } from "./RewardCard";
import { circleProgressPercent, circleProgressRemainingLine, circleProgressSummary } from "../utils/circleProgress";

// Applied reward first, then rewards you can use, then the ones that can't be used.
const rankOf = (offer, appliedReward) => {
  if (appliedReward?.id === Number(offer.id)) return 0;
  return offer.active === false ? 2 : 1;
};

/**
 * Full-screen page listing every Circle Reward (straight from the API) with its
 * Apply button. Opened from the cart's "Rewards" line; applying a reward closes
 * it and the cart shows the updated bill.
 */
export default function RewardsPage({
  onClose,
  circle,
  circleLoading,
  circleError,
  onRetryCircle,
  coins,
  coinsLoading,
  coinsError,
  reservedCoins,
  appliedReward,
  offers,
  offersLoading,
  offersError,
  applyError,
  applyingId,
  onSelect
}) {
  const isMember = Boolean(circle?.isMember);

  // Applied reward first, then the ones you can use now, then the locked ones —
  // locked ones nearest to unlocking (cheapest) first.
  const sortedOffers = [...(offers || [])].sort((a, b) => {
    const byRank = rankOf(a, appliedReward) - rankOf(b, appliedReward);
    if (byRank !== 0) return byRank;
    return rankOf(a, appliedReward) === 2 ? (Number(a.cost) || 0) - (Number(b.cost) || 0) : 0;
  });
  const readyOffers = sortedOffers.filter((offer) => rankOf(offer, appliedReward) < 2);
  const lockedOffers = sortedOffers.filter((offer) => rankOf(offer, appliedReward) === 2);
  const renderOffer = (offer) => (
    <RewardCard
      key={offer.id}
      offer={offer}
      applied={appliedReward?.id === Number(offer.id)}
      applying={applyingId === offer.id}
      saving={appliedReward?.id === Number(offer.id) ? appliedReward.amount : 0}
      coins={coinsLoading ? undefined : coins}
      onApply={onSelect}
      onRemove={onSelect}
    />
  );

  return (
    <div className="rewards-page billing-ui" role="dialog" aria-modal="true" aria-label="Rewards">
      <div className="rewards-page-header">
        <button type="button" className="rewards-page-back" onClick={onClose} aria-label="Back to cart">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h2 className="rewards-page-title">Rewards</h2>
      </div>

      <div className="rewards-page-body">
        {circleLoading || (!circle && !circleError) ? (
          <div className="reward-list" aria-busy="true">
            <RewardCardSkeleton />
            <RewardCardSkeleton />
          </div>
        ) : circleError ? (
          <>
            <p className="cart-step-error">{circleError}</p>
            <button type="button" className="cart-step-text-action" onClick={onRetryCircle}>Try again</button>
          </>
        ) : !isMember ? (
          circle.canJoin ? (
            <div className="cart-circle-card">
              <p className="cart-circle-title">Join Circle Membership</p>
              <p className="cart-circle-sub">You're eligible! Join to unlock Circle Coins and rewards on your orders.</p>
              <a className="cart-step-btn-primary cart-circle-join" href={circle.joinUrl} target="_blank" rel="noopener noreferrer">Join Member</a>
            </div>
          ) : circle.progress?.showMessage ? (
            <div className="cart-circle-card">
              <p className="cart-circle-title">Unlock Circle Membership</p>
              <p className="cart-circle-sub">{circleProgressRemainingLine(circle.progress)}</p>
              <div className="cart-circle-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={circleProgressPercent(circle.progress)}>
                <div className="cart-circle-bar-fill" style={{ width: `${circleProgressPercent(circle.progress)}%` }} />
              </div>
              <div className="cart-circle-summary">
                <span>{circleProgressSummary(circle.progress).orderDays}</span>
                <span>{circleProgressSummary(circle.progress).spend}</span>
              </div>
            </div>
          ) : (
            <p className="cart-step-hint">Circle rewards are available to Circle members.</p>
          )
        ) : (
          <>
            <div className="reward-balance">
              <CoinIcon size={26} />
              <span className="reward-balance-label">Your balance</span>
              <strong>{coinsLoading ? "…" : coins}</strong>
              <span>Circle Coins</span>
            </div>
            <p className="reward-howto">
              Pick a reward to take it off this bill. One reward per order, and your Circle Coins are
              only used when you pay.
            </p>
            {reservedCoins > 0 && !coinsLoading && (
              <p className="cart-step-hint">{reservedCoins} coins reserved for {appliedReward?.name}, deducted when you pay.</p>
            )}
            {circle.rewardsDisabled && (
              <p className="reward-notice">{circle.rewardsDisabledMessage || "Rewards are not available on this account."}</p>
            )}
            {coinsError && <p className="cart-step-error">{coinsError}</p>}

            {offersLoading ? (
              <div className="reward-list" aria-busy="true">
                <RewardCardSkeleton />
                <RewardCardSkeleton />
              </div>
            ) : offersError ? (
              <p className="cart-step-error">{offersError}</p>
            ) : offers.length === 0 ? (
              <p className="cart-step-hint">No rewards available right now.</p>
            ) : (
              <>
                {readyOffers.length > 0 && (
                  <>
                    {lockedOffers.length > 0 && (
                      <h3 className="reward-section-title">Ready to use <span>{readyOffers.length}</span></h3>
                    )}
                    <div className="reward-list">{readyOffers.map(renderOffer)}</div>
                  </>
                )}
                {lockedOffers.length > 0 && (
                  <>
                    <h3 className="reward-section-title reward-section-locked">Locked <span>{lockedOffers.length}</span></h3>
                    <p className="reward-section-sub">Not yet — here's what each one needs.</p>
                    <div className="reward-list">{lockedOffers.map(renderOffer)}</div>
                  </>
                )}
              </>
            )}
            {applyError && <p className="cart-step-error">{applyError}</p>}
          </>
        )}
      </div>
    </div>
  );
}
